import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Map,
  Satellite,
  Car,
  Train,
  Mountain,
  Tag,
  Route,
  RotateCcw,
  ChevronDown,
  Settings2,
  Type
} from 'lucide-react';
import '../styles/MapTypeToggle.css';

/**
 * MiniToggle - Elegant mini toggle switch component
 * Note: RTL layout - enabled state has thumb on left (x=16), disabled on right (x=0)
 */
const MiniToggle = ({ enabled, onChange, disabled = false }) => (
  <button
    className={`mini-toggle ${enabled ? 'enabled' : ''} ${disabled ? 'disabled' : ''}`}
    onClick={() => !disabled && onChange(!enabled)}
    disabled={disabled}
    type="button"
  >
    <motion.div
      className="mini-toggle-thumb"
      animate={{ x: enabled ? 16 : 0 }}
      transition={{ type: "spring", stiffness: 500, damping: 30 }}
    />
  </button>
);

/**
 * SettingRow - Individual setting row with icon, label, and toggle
 */
const SettingRow = ({ icon: Icon, label, enabled, onChange, disabled, delay = 0 }) => (
  <motion.div
    className={`setting-row ${disabled ? 'disabled' : ''}`}
    initial={{ opacity: 0, x: -10 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -10 }}
    transition={{ delay, duration: 0.2 }}
  >
    <div className="setting-info">
      <Icon className="setting-icon" />
      <span className="setting-label">{label}</span>
    </div>
    <MiniToggle enabled={enabled} onChange={onChange} disabled={disabled} />
  </motion.div>
);

/**
 * MapTypeToggle - Enhanced with expandable settings panel
 * Glass-morphism design with mode-specific options
 */
export default function MapTypeToggle({
  mapType,
  onMapTypeChange,
  mapSettings = {},
  onSettingsChange
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Default settings
  const defaultSettings = {
    // Map mode settings
    showTraffic: false,
    showTransit: false,
    showTerrain: false,
    hideMapLabels: false,
    // Satellite mode settings
    showLabels: true,
    showRoads: true,
    tilt45: false,
  };

  const settings = { ...defaultSettings, ...mapSettings };

  const handleSettingChange = (key, value) => {
    if (onSettingsChange) {
      onSettingsChange({ ...settings, [key]: value });
    }
  };

  const mapModeSettings = [
    {
      key: 'showTraffic',
      icon: Car,
      label: 'لایه ترافیک',
      enabled: settings.showTraffic
    },
    {
      key: 'showTransit',
      icon: Train,
      label: 'لایه حمل‌ونقل',
      enabled: settings.showTransit
    },
    {
      key: 'showTerrain',
      icon: Mountain,
      label: 'حالت توپوگرافی',
      enabled: settings.showTerrain
    },
    {
      key: 'hideMapLabels',
      icon: Type,
      label: 'حذف لیبل‌ها',
      enabled: settings.hideMapLabels
    },
  ];

  const satelliteModeSettings = [
    {
      key: 'showLabels',
      icon: Tag,
      label: 'نمایش برچسب‌ها',
      enabled: settings.showLabels
    },
    {
      key: 'showRoads',
      icon: Route,
      label: 'نمایش جاده‌ها',
      enabled: settings.showRoads
    },
    {
      key: 'tilt45',
      icon: RotateCcw,
      label: 'نمای ۴۵ درجه',
      enabled: settings.tilt45
    },
  ];

  const currentSettings = mapType === 'satellite' ? satelliteModeSettings : mapModeSettings;

  return (
    <motion.div
      className={`map-type-container ${isExpanded ? 'expanded' : ''}`}
      layout
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
    >
      {/* Main Toggle */}
      <div className="map-type-toggle">
        <button
          className={`map-type-btn ${mapType === 'roadmap' || mapType === 'terrain' ? 'active' : ''}`}
          onClick={() => onMapTypeChange('roadmap')}
          title="نقشه"
        >
          <Map className="map-type-icon" />
          <span>نقشه</span>
        </button>
        <button
          className={`map-type-btn ${mapType === 'satellite' || mapType === 'hybrid' ? 'active' : ''}`}
          onClick={() => onMapTypeChange('satellite')}
          title="ماهواره"
        >
          <Satellite className="map-type-icon" />
          <span>ماهواره</span>
        </button>
        <div
          className="map-type-slider"
          style={{
            transform: (mapType === 'satellite' || mapType === 'hybrid') ? 'translateX(-100%)' : 'translateX(0)'
          }}
        />
      </div>

      {/* Expand/Collapse Button */}
      <button
        className={`settings-expand-btn ${isExpanded ? 'expanded' : ''}`}
        onClick={() => setIsExpanded(!isExpanded)}
        title={isExpanded ? 'بستن تنظیمات' : 'تنظیمات نقشه'}
      >
        <Settings2 className="settings-expand-icon" />
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <ChevronDown className="chevron-icon" />
        </motion.div>
      </button>

      {/* Settings Panel */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            className="settings-panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          >
            <div className="settings-divider" />

            <div className="settings-header">
              <span className="settings-title">
                {mapType === 'satellite' || mapType === 'hybrid' ? 'تنظیمات ماهواره' : 'تنظیمات نقشه'}
              </span>
            </div>

            <div className="settings-list">
              {currentSettings.map((setting, index) => (
                <SettingRow
                  key={setting.key}
                  icon={setting.icon}
                  label={setting.label}
                  enabled={setting.enabled}
                  onChange={(value) => handleSettingChange(setting.key, value)}
                  disabled={setting.disabled}
                  delay={index * 0.05}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
